import React, { useEffect, useRef } from 'react';

interface BackgroundAnimationProps {
  // Optional props for customization
  color?: string;
  intensity?: number;
}

const BackgroundAnimation: React.FC<BackgroundAnimationProps> = ({ 
  color = '#0ED3CF', 
  intensity = 0.5 
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animRef = useRef<number>(0);
  const visibleRef = useRef(true);
  const reducedMotionRef = useRef(false);
  
  useEffect(() => {
    reducedMotionRef.current = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reducedMotionRef.current) return;
    
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    
    const setCanvasDimensions = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    
    window.addEventListener('resize', setCanvasDimensions);
    setCanvasDimensions();
    
    let mouseX = -1000;
    let mouseY = -1000;
    
    const handleMouseMove = (e: MouseEvent) => {
      mouseX = e.clientX;
      mouseY = e.clientY;
    };
    
    window.addEventListener('mousemove', handleMouseMove);
    
    const handleVisibilityChange = () => {
      visibleRef.current = document.visibilityState === 'visible';
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    const particleCount = 45; // Reduced from 100
    const maxDistance = 120;  // Reduced from 150
    const particles: Particle[] = [];
    
    class Particle {
      x: number;
      y: number;
      size: number;
      speedX: number;
      speedY: number;
      opacity: number;
      baseOpacity: number;
      
      constructor() {
        this.x = Math.random() * canvas.width;
        this.y = Math.random() * canvas.height;
        this.size = Math.random() * 3 + 1; // Smaller particles
        this.speedX = (Math.random() - 0.5) * 0.3; // Slower
        this.speedY = (Math.random() - 0.5) * 0.3;
        this.baseOpacity = Math.random() * 0.5 + 0.05;
        this.opacity = this.baseOpacity;
      }
      
      update() {
        const dx = mouseX - this.x;
        const dy = mouseY - this.y;
        const distance = Math.sqrt(dx * dx + dy * dy);
        
        if (distance < 120 && distance > 0) {
          const force = (120 - distance) / 120;
          this.speedX += (dx / distance) * force * 0.008;
          this.speedY += (dy / distance) * force * 0.008;
          this.opacity = Math.min(1, this.baseOpacity + force * 0.3);
        } else {
          this.opacity += (this.baseOpacity - this.opacity) * 0.05;
        }
        
        this.speedX *= 0.98;
        this.speedY *= 0.98;
        
        this.x += this.speedX;
        this.y += this.speedY;
        
        if (this.x > canvas.width) this.x = 0;
        else if (this.x < 0) this.x = canvas.width;
        if (this.y > canvas.height) this.y = 0;
        else if (this.y < 0) this.y = canvas.height;
      }
      
      draw() {
        ctx.fillStyle = color;
        ctx.globalAlpha = this.opacity * intensity;
        ctx.beginPath();
        ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2);
        ctx.fill();
      }
    }
    
    for (let i = 0; i < particleCount; i++) {
      particles.push(new Particle());
    }
    
    // Spatial hashing to avoid O(n²)
    const gridSize = maxDistance;
    const getGridKey = (x: number, y: number) => {
      const gx = Math.floor(x / gridSize);
      const gy = Math.floor(y / gridSize);
      return `${gx},${gy}`;
    };
    
    function connectParticles() {
      const grid = new Map<string, number[]>();
      
      for (let i = 0; i < particles.length; i++) {
        const key = getGridKey(particles[i].x, particles[i].y);
        if (!grid.has(key)) grid.set(key, []);
        grid.get(key)!.push(i);
      }
      
      for (let i = 0; i < particles.length; i++) {
        const p1 = particles[i];
        const gx = Math.floor(p1.x / gridSize);
        const gy = Math.floor(p1.y / gridSize);
        
        for (let ox = -1; ox <= 1; ox++) {
          for (let oy = -1; oy <= 1; oy++) {
            const key = `${gx + ox},${gy + oy}`;
            const neighbors = grid.get(key);
            if (!neighbors) continue;
            
            for (const j of neighbors) {
              if (j <= i) continue;
              const p2 = particles[j];
              const dx = p1.x - p2.x;
              const dy = p1.y - p2.y;
              const dist = dx * dx + dy * dy; // Skip sqrt for comparison
              
              if (dist < maxDistance * maxDistance) {
                const d = Math.sqrt(dist);
                ctx.beginPath();
                ctx.strokeStyle = color;
                ctx.globalAlpha = (1 - d / maxDistance) * 0.15 * intensity;
                ctx.lineWidth = 0.5;
                ctx.moveTo(p1.x, p1.y);
                ctx.lineTo(p2.x, p2.y);
                ctx.stroke();
              }
            }
          }
        }
      }
    }
    
    function animate() {
      if (!visibleRef.current) {
        animRef.current = requestAnimationFrame(animate);
        return;
      }
      
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      
      for (const particle of particles) {
        particle.update();
        particle.draw();
      }
      
      connectParticles();
      animRef.current = requestAnimationFrame(animate);
    }
    
    animate();
    
    return () => {
      window.removeEventListener('resize', setCanvasDimensions);
      window.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      cancelAnimationFrame(animRef.current);
    };
  }, [color, intensity]);
  
  if (typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    return null;
  }
  
    return (
    <canvas 
      ref={canvasRef}
      className="fixed inset-0" 
      style={{ 
        pointerEvents: 'none',
        zIndex: 5,
        opacity: 0.6
      }}
    />
  );
};

export default BackgroundAnimation;
